import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FaCookieBite,
  FaShieldAlt,
  FaChartBar,
  FaCogs,
  FaUserCheck,
  FaEye,
  FaTrash,
  FaCheckCircle,
  FaTimesCircle,
  FaInfoCircle,
  FaExclamationTriangle,
  FaDownload,
  FaLock,
  FaBell,
  FaGoogle
} from 'react-icons/fa';

const CookiePolicy = () => {
  const [consentGiven, setConsentGiven] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    essential: true,
    analytics: false,
    security: true,
    thirdParty: false
  });
  const [showSettings, setShowSettings] = useState(false);

  // Load consent from localStorage on component mount
  useEffect(() => {
    const savedConsent = localStorage.getItem('cookieConsent');
    const savedPreferences = localStorage.getItem('cookiePreferences');
    
    if (savedConsent === 'true') {
      setConsentGiven(true);
    }
    
    if (savedPreferences) {
      setCookiePreferences(JSON.parse(savedPreferences));
    }
  }, []);

  const handleConsent = (acceptAll = false) => {
    let preferences;
    
    if (acceptAll) {
      preferences = {
        essential: true,
        analytics: true,
        security: true,
        thirdParty: true
      };
    } else {
      preferences = cookiePreferences;
    }
    
    setCookiePreferences(preferences);
    setConsentGiven(true);
    
    // Save to localStorage
    localStorage.setItem('cookieConsent', 'true');
    localStorage.setItem('cookiePreferences', JSON.stringify(preferences));
    
    // In a real app, you would also set the actual cookies
    console.log('Cookie preferences saved:', preferences);
  };

  const handlePreferenceChange = (cookieType) => {
    if (cookieType === 'essential') return; // Essential cookies cannot be disabled
    
    setCookiePreferences(prev => ({
      ...prev,
      [cookieType]: !prev[cookieType]
    }));
  };

  const handleResetCookies = () => {
    // Clear all cookies (in a real app, you would clear actual cookies)
    localStorage.removeItem('cookieConsent');
    localStorage.removeItem('cookiePreferences');
    setConsentGiven(false);
    setCookiePreferences({
      essential: true,
      analytics: false,
      security: true,
      thirdParty: false
    });
    alert('Cookie preferences have been reset. Please refresh the page.');
  };

  const cookieTypes = [
    {
      id: 'essential',
      name: 'Essential Cookies',
      icon: <FaCogs />,
      color: 'blue',
      description: 'Required for basic website functionality',
      alwaysEnabled: true,
      examples: ['session_id', 'user_preferences', 'shopping_cart']
    },
    {
      id: 'analytics',
      name: 'Analytics Cookies',
      icon: <FaChartBar />,
      color: 'green',
      description: 'Help us understand how visitors interact with our website',
      alwaysEnabled: false,
      examples: ['_ga', '_gid', 'utm_source']
    },
    {
      id: 'security',
      name: 'Security Cookies',
      icon: <FaLock />,
      color: 'red',
      description: 'Protect against fraud and ensure secure transactions',
      alwaysEnabled: true,
      examples: ['csrf_token', 'auth_token', 'security_session']
    },
    {
      id: 'thirdParty',
      name: 'Third-Party Cookies',
      icon: <FaGoogle />,
      color: 'purple',
      description: 'Set by external services like payment gateways and analytics',
      alwaysEnabled: false,
      examples: ['__stripe_mid', 'razorpay_session', 'facebook_pixel']
    }
  ];

  const thirdPartyServices = [
    {
      name: 'Razorpay',
      purpose: 'Payment processing and fraud prevention',
      cookies: ['rzp_device_id', 'rzp_checkout_session'],
      policy: 'https://razorpay.com/privacy/'
    },
    {
      name: 'Google Analytics',
      purpose: 'Website analytics and visitor tracking',
      cookies: ['_ga', '_gid', '_gat'],
      policy: 'https://policies.google.com/privacy'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-amber-50">
      {/* Cookie Consent Banner */}
      {!consentGiven && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50 p-4 md:p-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  <FaCookieBite className="text-amber-600 mr-2" />
                  <h3 className="font-bold text-gray-800">Cookie Consent</h3>
                </div>
                <p className="text-gray-600 text-sm">
                  We use cookies to enhance your browsing experience, analyze site traffic, 
                  and personalize content. By clicking "Accept All", you consent to our use of cookies.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setShowSettings(true)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Customize Settings
                </button>
                <button
                  onClick={() => handleConsent(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Accept Necessary
                </button>
                <button
                  onClick={() => handleConsent(true)}
                  className="px-4 py-2 bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-lg hover:from-amber-700 hover:to-orange-700 transition-colors"
                >
                  Accept All Cookies
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cookie Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800 flex items-center">
                  <FaCookieBite className="mr-3 text-amber-600" />
                  Cookie Settings
                </h2>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-6">
                {cookieTypes.map((type) => (
                  <div key={type.id} className="p-4 border border-gray-200 rounded-xl">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center">
                        <div className={`p-2 bg-${type.color}-100 rounded-lg mr-3`}>
                          <span className={`text-${type.color}-600`}>{type.icon}</span>
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-800">{type.name}</h4>
                          <p className="text-sm text-gray-600">{type.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center">
                        {type.alwaysEnabled ? (
                          <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full">
                            Always On
                          </span>
                        ) : (
                          <label className="flex items-center cursor-pointer">
                            <div className="relative">
                              <input
                                type="checkbox"
                                checked={cookiePreferences[type.id]}
                                onChange={() => handlePreferenceChange(type.id)}
                                className="sr-only"
                              />
                              <div className={`block w-12 h-6 rounded-full ${cookiePreferences[type.id] ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                              <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${cookiePreferences[type.id] ? 'transform translate-x-6' : ''}`}></div>
                            </div>
                            <span className="ml-3 text-gray-700">
                              {cookiePreferences[type.id] ? 'On' : 'Off'}
                            </span>
                          </label>
                        )}
                      </div>
                    </div>
                    
                    <div className="pl-14">
                      <p className="text-sm text-gray-600 mb-2">Examples:</p>
                      <div className="flex flex-wrap gap-2">
                        {type.examples.map((example, index) => (
                          <span key={index} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                            {example}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => setShowSettings(false)}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      handleConsent(false);
                      setShowSettings(false);
                    }}
                    className="px-6 py-2 bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-lg hover:from-amber-700 hover:to-orange-700"
                  >
                    Save Preferences
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-amber-600 to-orange-700 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold mb-3 flex items-center">
                <FaCookieBite className="mr-3" />
                Cookie Policy
              </h1>
              <p className="text-amber-100 text-lg">
                Understanding how we use cookies to enhance your experience
              </p>
            </div>
            <div className="mt-6 md:mt-0">
              <button
                onClick={() => setShowSettings(true)}
                className="bg-white text-amber-600 px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors flex items-center"
              >
                <FaCogs className="mr-2" />
                Manage Cookie Preferences
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:w-1/4">
            <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-8">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-gray-800 mb-4">Quick Links</h2>
                <nav className="space-y-2">
                  {['what', 'types', 'why', 'thirdparty', 'control'].map((section) => (
                    <a
                      key={section}
                      href={`#${section}`}
                      className="block px-4 py-3 text-gray-600 hover:text-amber-600 hover:bg-amber-50 rounded-xl transition-colors"
                    >
                      {section === 'what' && 'What Are Cookies'}
                      {section === 'types' && 'Types of Cookies'}
                      {section === 'why' && 'Why We Use Cookies'}
                      {section === 'thirdparty' && 'Third-Party Cookies'}
                      {section === 'control' && 'Cookie Control'}
                    </a>
                  ))}
                </nav>
              </div>

              <div className="p-4 bg-amber-50 rounded-xl">
                <div className="flex items-start">
                  <FaBell className="text-amber-500 mt-1 mr-3" />
                  <div>
                    <h4 className="font-bold text-amber-800 mb-1">Current Status</h4>
                    <p className="text-sm text-amber-700">
                      {consentGiven ? '✅ Consent Given' : '⏳ Pending Consent'}
                    </p>
                    {consentGiven && (
                      <button
                        onClick={handleResetCookies}
                        className="text-xs text-amber-600 hover:text-amber-800 mt-2"
                      >
                        Reset Preferences
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:w-3/4">
            {/* What Are Cookies */}
            <div id="what" className="bg-white rounded-2xl shadow-lg p-8 mb-8">
              <div className="flex items-center mb-6">
                <div className="p-3 bg-amber-100 rounded-xl mr-4">
                  <FaInfoCircle className="text-amber-600 text-2xl" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">What Are Cookies?</h2>
                  <p className="text-gray-600 mt-1">Understanding Digital Cookies</p>
                </div>
              </div>
              
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 leading-relaxed mb-4">
                  Cookies are small text files that are placed on your computer, smartphone, 
                  or other device when you visit a website. They are widely used to make 
                  websites work more efficiently and provide information to the website owners.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                  <div className="p-4 border border-amber-200 rounded-xl bg-amber-50">
                    <h4 className="font-bold text-amber-800 mb-2 flex items-center">
                      <FaCheckCircle className="mr-2" />
                      How Cookies Help
                    </h4>
                    <ul className="text-amber-700 space-y-2">
                      <li>Remember your preferences</li>
                      <li>Keep you logged in</li>
                      <li>Improve website speed</li>
                      <li>Personalize content</li>
                    </ul>
                  </div>

                  <div className="p-4 border border-gray-200 rounded-xl bg-gray-50">
                    <h4 className="font-bold text-gray-800 mb-2 flex items-center">
                      <FaEye className="mr-2" />
                      What Cookies Are NOT
                    </h4>
                    <ul className="text-gray-700 space-y-2">
                      <li>Not viruses or malware</li>
                      <li>Not programs that run on your computer</li>
                      <li>Cannot access personal files</li>
                      <li>Cannot transmit viruses</li>
                    </ul>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl">
                  <div className="flex items-start">
                    <FaExclamationTriangle className="text-blue-500 mt-1 mr-3" />
                    <div>
                      <h4 className="font-bold text-blue-800 mb-1">Important Note</h4>
                      <p className="text-blue-700">
                        Cookies do not contain personally identifiable information like your 
                        name, address, or credit card details. They only store anonymous data 
                        related to your browsing session.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Types of Cookies */}
            <div id="types" className="bg-white rounded-2xl shadow-lg p-8 mb-8">
              <div className="flex items-center mb-6">
                <div className="p-3 bg-blue-100 rounded-xl mr-4">
                  <FaCogs className="text-blue-600 text-2xl" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">Types of Cookies We Use</h2>
                  <p className="text-gray-600 mt-1">Classification Based on Purpose</p>
                </div>
              </div>

              <div className="space-y-6">
                {cookieTypes.map((type) => (
                  <div key={type.id} className={`p-4 border border-${type.color}-200 rounded-xl bg-${type.color}-50`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center">
                        <div className={`p-2 bg-${type.color}-100 rounded-lg mr-3`}>
                          <span className={`text-${type.color}-600`}>{type.icon}</span>
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-800">{type.name}</h4>
                          <p className="text-sm text-gray-600">{type.description}</p>
                        </div>
                      </div>
                      <span className={`px-3 py-1 bg-${type.color}-100 text-${type.color}-700 text-sm rounded-full`}>
                        {type.alwaysEnabled ? 'Always Active' : 'Optional'}
                      </span>
                    </div>
                    
                    <div className="ml-14">
                      <div className="flex items-center mb-2">
                        <span className={`w-2 h-2 rounded-full bg-${type.color}-500 mr-2`}></span>
                        <span className="text-sm font-medium text-gray-700">Purpose:</span>
                      </div>
                      <p className="text-gray-600 text-sm mb-3">
                        {type.id === 'essential' && 'Enable core functionality like page navigation and access to secure areas'}
                        {type.id === 'analytics' && 'Collect anonymous data about how visitors use our website'}
                        {type.id === 'security' && 'Protect user data and prevent fraudulent activities'}
                        {type.id === 'thirdParty' && 'Enable third-party services to function properly'}
                      </p>
                      
                      <div className="flex items-center mb-2">
                        <span className={`w-2 h-2 rounded-full bg-${type.color}-500 mr-2`}></span>
                        <span className="text-sm font-medium text-gray-700">Examples:</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {type.examples.map((example, index) => (
                          <span key={index} className="px-2 py-1 bg-white text-gray-700 text-xs rounded border">
                            {example}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Why We Use Cookies */}
            <div id="why" className="bg-white rounded-2xl shadow-lg p-8 mb-8">
              <div className="flex items-center mb-6">
                <div className="p-3 bg-green-100 rounded-xl mr-4">
                  <FaChartBar className="text-green-600 text-2xl" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">Why We Use Cookies</h2>
                  <p className="text-gray-600 mt-1">Purpose and Benefits</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="p-4 border border-green-200 rounded-xl bg-green-50">
                  <h4 className="font-bold text-green-800 mb-3 flex items-center">
                    <FaCogs className="mr-2" />
                    Website Performance
                  </h4>
                  <ul className="text-green-700 space-y-2 text-sm">
                    <li className="flex items-center">
                      <FaCheckCircle className="text-green-500 mr-2" size={12} />
                      Faster page loading
                    </li>
                    <li className="flex items-center">
                      <FaCheckCircle className="text-green-500 mr-2" size={12} />
                      Reduced server load
                    </li>
                    <li className="flex items-center">
                      <FaCheckCircle className="text-green-500 mr-2" size={12} />
                      Error prevention
                    </li>
                    <li className="flex items-center">
                      <FaCheckCircle className="text-green-500 mr-2" size={12} />
                      Traffic optimization
                    </li>
                  </ul>
                </div>

                <div className="p-4 border border-blue-200 rounded-xl bg-blue-50">
                  <h4 className="font-bold text-blue-800 mb-3 flex items-center">
                    <FaUserCheck className="mr-2" />
                    User Experience
                  </h4>
                  <ul className="text-blue-700 space-y-2 text-sm">
                    <li className="flex items-center">
                      <FaCheckCircle className="text-blue-500 mr-2" size={12} />
                      Remember preferences
                    </li>
                    <li className="flex items-center">
                      <FaCheckCircle className="text-blue-500 mr-2" size={12} />
                      Personalized content
                    </li>
                    <li className="flex items-center">
                      <FaCheckCircle className="text-blue-500 mr-2" size={12} />
                      Stay logged in
                    </li>
                    <li className="flex items-center">
                      <FaCheckCircle className="text-blue-500 mr-2" size={12} />
                      Language selection
                    </li>
                  </ul>
                </div>

                <div className="p-4 border border-red-200 rounded-xl bg-red-50">
                  <h4 className="font-bold text-red-800 mb-3 flex items-center">
                    <FaLock className="mr-2" />
                    Payment Security
                  </h4>
                  <ul className="text-red-700 space-y-2 text-sm">
                    <li className="flex items-center">
                      <FaCheckCircle className="text-red-500 mr-2" size={12} />
                      Fraud prevention
                    </li>
                    <li className="flex items-center">
                      <FaCheckCircle className="text-red-500 mr-2" size={12} />
                      Secure sessions
                    </li>
                    <li className="flex items-center">
                      <FaCheckCircle className="text-red-500 mr-2" size={12} />
                      Transaction verification
                    </li>
                    <li className="flex items-center">
                      <FaCheckCircle className="text-red-500 mr-2" size={12} />
                      Data encryption
                    </li>
                  </ul>
                </div>
              </div>

              <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl">
                <div className="flex items-start">
                  <FaInfoCircle className="text-amber-500 mt-1 mr-3" />
                  <div>
                    <h4 className="font-bold text-amber-800 mb-1">Cookie Lifespan</h4>
                    <p className="text-amber-700">
                      Cookies have different lifespans. Session cookies expire when you close 
                      your browser, while persistent cookies remain for a set period (usually 
                      30 days to 2 years) unless you delete them manually.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Third-Party Cookies */}
            <div id="thirdparty" className="bg-white rounded-2xl shadow-lg p-8 mb-8">
              <div className="flex items-center mb-6">
                <div className="p-3 bg-purple-100 rounded-xl mr-4">
                  <FaGoogle className="text-purple-600 text-2xl" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">Third-Party Cookies</h2>
                  <p className="text-gray-600 mt-1">External Services We Use</p>
                </div>
              </div>

              <div className="space-y-4 mb-6">
                {thirdPartyServices.map((service, index) => (
                  <div key={index} className="p-4 border border-purple-200 rounded-xl">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="font-bold text-purple-800 text-lg">{service.name}</h4>
                        <p className="text-purple-700 text-sm">{service.purpose}</p>
                      </div>
                      <a
                        href={service.policy}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        View Privacy Policy →
                      </a>
                    </div>
                    
                    <div className="mt-3">
                      <p className="text-sm font-medium text-gray-700 mb-2">Cookies Used:</p>
                      <div className="flex flex-wrap gap-2">
                        {service.cookies.map((cookie, idx) => (
                          <span key={idx} className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded">
                            {cookie}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-4 bg-gray-50 rounded-xl">
                <div className="flex items-start">
                  <FaExclamationTriangle className="text-gray-500 mt-1 mr-3" />
                  <div>
                    <h4 className="font-bold text-gray-800 mb-1">Important Notice</h4>
                    <p className="text-gray-700">
                      We do not control the cookies set by third-party services. Their use of 
                      cookies is governed by their own privacy policies. We recommend reviewing 
                      their policies to understand how they use your data.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Cookie Control */}
            <div id="control" className="bg-white rounded-2xl shadow-lg p-8">
              <div className="flex items-center mb-6">
                <div className="p-3 bg-teal-100 rounded-xl mr-4">
                  <FaGoogle className="text-teal-600 text-2xl" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">Cookie Control & Browser Settings</h2>
                  <p className="text-gray-600 mt-1">Managing Your Cookie Preferences</p>
                </div>
              </div>

              <div className="space-y-6">
                <div className="p-4 border border-teal-200 rounded-xl">
                  <h4 className="font-bold text-teal-800 mb-3 flex items-center">
                    <FaUserCheck className="mr-2" />
                    User Consent
                  </h4>
                  <p className="text-teal-700 mb-3">
                    When you first visit our website, you will be presented with a cookie consent 
                    banner. You can choose to:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-3 bg-green-50 rounded-lg">
                      <div className="flex items-center mb-2">
                        <FaCheckCircle className="text-green-500 mr-2" />
                        <span className="font-medium text-green-700">Accept All Cookies</span>
                      </div>
                      <p className="text-green-600 text-sm">
                        Accepts essential, analytics, security, and third-party cookies
                      </p>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <div className="flex items-center mb-2">
                        <FaCogs className="text-blue-500 mr-2" />
                        <span className="font-medium text-blue-700">Customize Settings</span>
                      </div>
                      <p className="text-blue-600 text-sm">
                        Choose which types of cookies to accept or reject
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-4 border border-gray-200 rounded-xl">
                  <h4 className="font-bold text-gray-800 mb-3 flex items-center">
                    <FaGoogle className="mr-2" />
                    Browser Settings
                  </h4>
                  <p className="text-gray-700 mb-3">
                    You can also control cookies through your browser settings. Most browsers allow you to:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h5 className="font-medium text-gray-700 mb-2">Common Browser Controls:</h5>
                      <ul className="text-gray-600 space-y-1 text-sm">
                        <li>• Block all cookies</li>
                        <li>• Block third-party cookies</li>
                        <li>• Clear existing cookies</li>
                        <li>• Set cookie expiration</li>
                      </ul>
                    </div>
                    <div>
                      <h5 className="font-medium text-gray-700 mb-2">How to Access:</h5>
                      <ul className="text-gray-600 space-y-1 text-sm">
                        <li>Chrome: Settings → Privacy and security</li>
                        <li>Firefox: Options → Privacy & Security</li>
                        <li>Safari: Preferences → Privacy</li>
                        <li>Edge: Settings → Cookies and permissions</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-gradient-to-r from-red-50 to-pink-50 rounded-xl">
                  <div className="flex items-start">
                    <FaExclamationTriangle className="text-red-500 mt-1 mr-3" />
                    <div>
                      <h4 className="font-bold text-red-800 mb-1">Important Consideration</h4>
                      <p className="text-red-700">
                        Blocking or deleting cookies may affect your ability to use certain 
                        features of our website. Essential cookies are required for basic 
                        functionality and cannot be disabled.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                  <div>
                    <p className="text-gray-600 text-sm">
                      Need help managing cookies? Contact our support team.
                    </p>
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => setShowSettings(true)}
                      className="px-6 py-2 border border-amber-300 text-amber-700 rounded-lg hover:bg-amber-50 transition-colors"
                    >
                      Update Preferences
                    </button>
                    <button
                      onClick={handleResetCookies}
                      className="px-6 py-2 bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-lg hover:from-amber-700 hover:to-orange-700 transition-colors"
                    >
                      Reset All Cookies
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-8 p-6 bg-white rounded-xl shadow border-t">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <p className="text-gray-600 text-sm">
                Last Updated: January 15, 2024
              </p>
            </div>
            <div className="flex space-x-6">
              <Link to="/privacy-policy" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                Privacy Policy
              </Link>
              <Link to="/terms-of-service" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                Terms of Service
              </Link>
              <Link to="/contact" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;